import { logoutAndRemoveWallet } from './currentUser/currentUserUtils';
import { userSigningOut } from './currentUserData/userProcesses';
import {
  StorageDelegatedAuthorization,
  StorageDelegatedSettings,
  loadStoredDelegatedAuthorization,
  storeDelegatedAuthorization,
} from './delegatedKey/cachingDelegatedKey';
import { staticDispatch } from './store';
import { ReduxState, ReduxStateShape } from './storeCore';
import { defaultUserSettingsState } from './userSettings/userSettingsReducer';
import { isWallet, WalletShape, EMPTY_WALLET } from './wallet/walletUtils';

export const REDUX_CACHE_ID = 'katana_app_v1';

/** Previous storage ID - to be removed from browser memory */
let OLD_STORAGE_CACHE_IDS = ['kuma_app_v1'];

type LocalStorageState = {
  wallet: {
    updated?: number;
    walletType:
      | 'softwareDiscovered'
      | 'softwareDiscovered_metaMask'
      | 'softwareDiscovered_trustWallet'
      | 'softwareWeb3'
      | 'coinbaseWallet'
      | 'walletConnect'
      | 'qrSynced'
      | 'privateKey'
      | 'keystore'
      | undefined
      | null;
    walletAddress: undefined | string;
    walletAddressOriginal: undefined | string;
    walletDiscoveryRdns?: string;
    walletPath?: undefined | string;
    walletKey?: undefined | string;
    walletKeyPEncryption?: 'unlock' | 'personal1'; // personal encryption
  };
  walletSectionMobileHeaderExpanded: boolean;
  selectedChartType?: 'depth' | 'tradingView';
  pendingDepositsV3: string;
  userMarketTickSize?: { [market: string]: string };
  userHideOtherMarkets: boolean;
  // DEMO
  // demoStarted: boolean;
  // demoMobileAnnouceClosed: boolean;
  // demoReferral: string;
  // demoNeedDisplayBanner?: boolean;

  notices: ReduxState['notices'];
  region: {
    restrictedRegion: false | string;
    currentRegion: string;
    userRegion: string;
  };
  userSettingsV2: {
    gdprConsent?: number;
    // favoriteMarkets?: string[];
    dismissedNotices?: string[];
  };
  userReferralCode?: string;
  userBankrollCode?: string;
  userBankrollCodeType?: 'bankroll' | 'welcomePoints';

  // User guide
  // firstBalancesAfterUnlock: boolean;
  // closedIncorrectWalletChainId?: boolean;
  closedLargeSpreadMarketOrderWarning?: boolean;
  closedIsolatedMarginWarning?: boolean;
  closedWelcomeModal?: boolean;
  skipBuySellConfirm?: boolean;
  skipCancelOrderConfirm?: boolean;
  skipQrConnectModal?: boolean;
  notifDisabled?: boolean;
  delegatedKeySettings?: StorageDelegatedSettings;
  // Referral code
  // referral?: string;
};

const serialize = (state: ReduxState): LocalStorageState => {
  const { userGuide } = state.exchange;
  return {
    wallet: {
      updated: state.wallet.updated,
      walletAddress: state.wallet.walletAddress,
      walletAddressOriginal: state.wallet.walletAddressOriginal,
      walletType: state.wallet.walletType || null,
      walletDiscoveryRdns: state.wallet.walletDiscoveryRdns,
      walletPath: state.wallet.hardwareWalletPath,
      walletKey: state.wallet.privateKeyEncrypted,
      walletKeyPEncryption: state.wallet.privateKeyPersonalEncryption,
    },
    walletSectionMobileHeaderExpanded:
      state.walletSection.mobileHeaderStatsExpanded,
    pendingDepositsV3: JSON.stringify(state.balances.pendingDeposits),
    selectedChartType: state.exchange.selectedChart.chartType,

    // demoStarted: state.demo.demoStarted,
    // demoReferral: state.demo.referralCode,
    // demoMobileAnnouceClosed: state.demo.mobileAnnouceBannerClosed,
    // demoNeedDisplayBanner: state.demo.needDisplayBanner,

    notices: state.notices,
    region: state.region,

    userSettingsV2: {
      dismissedNotices: state.userSettings.dismissedNotices,
      // favoriteMarkets: state.userSettings.favoriteMarkets,
    },
    userMarketTickSize: state.markets.userMarketTickSize,
    userHideOtherMarkets: state.markets.userHideOtherMarkets,

    userReferralCode: state.currentUserData.userReferral.referralCode,
    userBankrollCode: state.currentUserData.userBankroll.bankrollCodeToApply,
    userBankrollCodeType: state.currentUserData.userBankroll.bankrollCodeType,

    // firstBalancesAfterUnlock: userGuide.visitedBalancesPageAfterFirstUnock,
    // closedIncorrectWalletChainId: userGuide.closedIncorrectWalletChainId,
    closedLargeSpreadMarketOrderWarning:
      userGuide.closedLargeSpreadMarketOrderWarning,
    closedIsolatedMarginWarning: userGuide.closedIsolatedMarginWarning,
    closedWelcomeModal: userGuide.closedWelcomeModal,
    skipBuySellConfirm: userGuide.skipBuySellConfirmation,
    skipCancelOrderConfirm: userGuide.skipCancelOrderConfirm,
    skipQrConnectModal: userGuide.skipQrConnectModal,
    notifDisabled: userGuide.notificationsDisabled,
    delegatedKeySettings: state.delegatedKey.settings,
    // referral: state.currentUserData.userReferral.referralCode,
  };
};

const DEFAULT_STATE_FALLBACK = undefined;

const deserialize = (
  serialized: LocalStorageState,
  delegatedAuthorization?: StorageDelegatedAuthorization
): ReduxStateShape | undefined => {
  let pendingDeposits;
  try {
    pendingDeposits = JSON.parse(serialized.pendingDepositsV3);
  } catch (error) {
    console.warn(error);
  }

  const deserializedWallet: WalletShape = {
    updated: serialized.wallet.updated,
    walletAddress: serialized.wallet.walletAddress,
    walletAddressOriginal: serialized.wallet.walletAddressOriginal,
    walletType: serialized.wallet.walletType,
    privateKeyEncrypted: serialized.wallet.walletKey,
    privateKeyPersonalEncryption:
      serialized.wallet.walletKeyPEncryption || undefined,
    hardwareWalletPath: serialized.wallet.walletPath,
    walletDiscoveryRdns: serialized.wallet.walletDiscoveryRdns,
  };

  // TypeGuard which guarantees a valid wallet type
  const wallet = isWallet(deserializedWallet)
    ? deserializedWallet
    : EMPTY_WALLET;

  return {
    wallet,
    walletSection: {
      mobileHeaderStatsExpanded: serialized.walletSectionMobileHeaderExpanded,
    },
    balances: {
      pendingDeposits,
    },
    markets: {
      currentMarket: DEFAULT_STATE_FALLBACK,
      marketsSidebar: DEFAULT_STATE_FALLBACK,
      userMarketTickSize:
        serialized.userMarketTickSize || DEFAULT_STATE_FALLBACK,
      userHideOtherMarkets:
        serialized.userHideOtherMarkets ?? DEFAULT_STATE_FALLBACK,
    },
    // demo: {
    //   demoStarted: !!serialized.demoStarted || false,
    //   referralCode: serialized.demoReferral || '',
    //   needDisplayBanner: serialized.demoNeedDisplayBanner || undefined,
    //   mobileAnnouceBannerClosed: serialized.demoMobileAnnouceClosed || false,
    // },
    notices: {
      bannersGlobal: serialized.notices.bannersGlobal || {},
      notificationsGlobal: serialized.notices.notificationsGlobal || {},
      customNotifications: serialized.notices.customNotifications || {},
    },
    region: serialized.region,
    userSettings: {
      dismissedNotices: serialized.userSettingsV2.dismissedNotices || [],
      // favoriteMarkets: serialized.userSettingsV2.favoriteMarkets || [],
      gdprConsent: defaultUserSettingsState.gdprConsent,
      settingsOpen: false,
    },
    currentUserData: {
      userBankroll: {
        bankrollCodeToApply: serialized.userBankrollCode,
        bankrollCodeType: serialized.userBankrollCodeType,
      },
      userReferral: {
        referralCode: serialized.userReferralCode,
      },
    },
    exchange: {
      userGuide: {
        // visitedBalancesPageAfterFirstUnock: serialized.firstBalancesAfterUnlock,
        // closedIncorrectWalletChainId: serialized.closedIncorrectWalletChainId,
        closedLargeSpreadMarketOrderWarning:
          serialized.closedLargeSpreadMarketOrderWarning,
        closedIsolatedMarginWarning: serialized.closedIsolatedMarginWarning,
        closedWelcomeModal: serialized.closedWelcomeModal,
        skipBuySellConfirmation: !!serialized.skipBuySellConfirm,
        skipCancelOrderConfirm: !!serialized.skipCancelOrderConfirm,
        skipQrConnectModal: !!serialized.skipQrConnectModal,
        notificationsDisabled: !!serialized.notifDisabled,
      },
      selectedChart: serialized.selectedChartType
        ? { chartType: serialized.selectedChartType }
        : DEFAULT_STATE_FALLBACK,
    },
    delegatedKey: {
      authorization: delegatedAuthorization || DEFAULT_STATE_FALLBACK,
      settings: serialized?.delegatedKeySettings || DEFAULT_STATE_FALLBACK,
    },
  };
};

export const loadState = (): ReduxStateShape | undefined => {
  // Wipe previous one from storage
  OLD_STORAGE_CACHE_IDS.forEach(id => localStorage.removeItem(id));
  OLD_STORAGE_CACHE_IDS = [];

  try {
    const serializedState = localStorage.getItem(REDUX_CACHE_ID);
    if (!serializedState) {
      return;
    }
    const state: LocalStorageState = JSON.parse(serializedState);
    if (typeof state !== 'object') {
      console.warn('Incorrect state', state);
      return;
    }
    const delegatedAuthorization = loadStoredDelegatedAuthorization(
      state.delegatedKeySettings
    );
    if (delegatedAuthorization) {
      return deserialize(state, delegatedAuthorization);
    }
    const { wallet } = state;
    // If temporary DK ("session") was active - we have initialize logout and
    // prevent any app interaction until API session and wallet is removed.
    if (state.delegatedKeySettings?.engine === 'session') {
      setTimeout(() => {
        staticDispatch(userSigningOut());
        logoutAndRemoveWallet({
          currentUser: undefined,
          walletType: wallet.walletType,
        });
      });
      return;
    }

    // If there is no Delegated authorization available, clear settings too
    delete state.delegatedKeySettings;
    return deserialize(state);
  } catch (error) {
    console.warn(error);
  }
};

let lastStored: string | undefined;

export const saveState = (state: ReduxState) => {
  try {
    const serializedState = JSON.stringify(serialize(state));
    if (lastStored === serializedState) {
      // No changes to state which should be stored
      return;
    }
    lastStored = serializedState;
    localStorage.setItem(REDUX_CACHE_ID, serializedState);
  } catch (error) {
    console.warn(error);
  }
  storeDelegatedAuthorization(
    state.delegatedKey.settings,
    state.delegatedKey.authorization
  );
};
